import type {
  AnalyticsEvent,
  AnalyticsEventParamsOf,
} from "@v2/features/infra/analytics/events";

import * as React from "react";

import { errorToast } from "@v1/components/designSystem/alert/Toast";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";

type ErrorEventType = Extract<AnalyticsEvent["type"], `error.${string}`>;

type BlockedEventType = Exclude<
  Extract<AnalyticsEvent["type"], `${string}.blocked`>,
  ErrorEventType
>;

export const useV1ErrorToast = () => {
  const logEvent = useLogAnalytics();

  return React.useCallback(
    (
      title: string,
      message: string,
      eventName: ErrorEventType,
      params?: AnalyticsEventParamsOf<ErrorEventType>,
    ) => {
      logEvent(eventName, params);
      return errorToast(title, message);
    },
    [logEvent],
  );
};

/**
 * Error-styled toast for a guard rail the user can simply retry (publishing while a save is in
 * flight), so it records a `*.blocked` event instead of an `error.*` one.
 */
export const useV1ExpectedErrorToast = () => {
  const logEvent = useLogAnalytics();

  return React.useCallback(
    <EventName extends BlockedEventType>(
      title: string,
      message: string,
      eventName: EventName,
      params: AnalyticsEventParamsOf<EventName>,
    ) => {
      logEvent(eventName, params);
      return errorToast(title, message);
    },
    [logEvent],
  );
};
